import { useActivity } from '../activity/api';
import { Target } from '../activity/activity-overview';
import { useOwnActivityLookups } from '../activity/my-activity';
import { actionLabel, changeLines } from '../activity/present';
import { HistoryIcon } from '../../components/icons';
import { LoadingState } from '../../components/states';
import {
  ActivityRow,
  AttentionSection,
  BackupsPanel,
  DashboardHeader,
  NotConnectedSection,
  Panel,
  PanelEmpty,
  PaymentAccountPanel,
  RenewalsSection,
  SectionError,
  ServicesSection,
  StatusOverview,
  ViewAllLink,
} from './dashboard';

/**
 * ICT Dashboard (/app/dashboard for the ICT view, internal role `operations`). Built only from the
 * endpoints ICT may use — services, upcoming renewals, its own activity, the Payment Account and the
 * mailbox endpoint's state — and shown exactly as the API reports it. It shares the Admin
 * Dashboard's building blocks; nothing Admin-only is requested or shown.
 */
export function IctDashboard() {
  return (
    <div className="space-y-6">
      <DashboardHeader />
      <StatusOverview />
      <AttentionSection view="ict" />
      <ServicesSection />
      <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
        <RenewalsSection />
        <MyRecentActivitySection />
        <PaymentAccountPanel view="ict" />
        <BackupsPanel />
      </div>
      <NotConnectedSection view="ict" />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// My recent activity — the API returns only ICT's own entries (audit.view_own)
// ---------------------------------------------------------------------------------------------

const ACTIVITY_SHOWN = 5;

function MyRecentActivitySection() {
  const activity = useActivity();
  // Every entry here is ICT's own (the API's rule), so the same lookups as My Activity apply.
  const lookups = useOwnActivityLookups();
  const entries = (activity.data?.pages[0]?.items ?? []).slice(0, ACTIVITY_SHOWN);

  return (
    <Panel
      title="My recent activity"
      icon={HistoryIcon}
      action={<ViewAllLink to="/app/my-activity">All my activity</ViewAllLink>}
    >
      {activity.isPending ? (
        <LoadingState label="Loading your activity…" />
      ) : activity.isError ? (
        <SectionError
          message={`Your activity could not be loaded: ${activity.error.message}`}
          onRetry={() => void activity.refetch()}
        />
      ) : entries.length === 0 ? (
        <PanelEmpty icon={HistoryIcon} title="No activity yet">
          No activity has been recorded for you yet.
        </PanelEmpty>
      ) : (
        <ol aria-label="My recent activity" className="space-y-3.5">
          {entries.map((entry) => {
            const lines = changeLines(entry);
            return (
              <ActivityRow
                key={entry.id}
                title={actionLabel(entry.action)}
                occurredAt={entry.occurredAt}
              >
                <div className="mt-0.5 break-words text-xs text-fg-2">
                  <Target entry={entry} lookups={lookups} />
                </div>
                {lines.length > 0 ? (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-xs font-medium text-muted">
                      Recorded details
                    </summary>
                    <dl className="mt-1 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-[max-content_1fr]">
                      {lines.map((line, index) => (
                        <div key={`${line.field}-${index}`} className="contents">
                          <dt className="font-medium text-muted">{line.field}</dt>
                          <dd className="break-all font-mono text-fg-2">{line.text}</dd>
                        </div>
                      ))}
                    </dl>
                  </details>
                ) : null}
              </ActivityRow>
            );
          })}
        </ol>
      )}
    </Panel>
  );
}
